import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import codingImage from '../assets/images/smilling_man_and_woman_writing_code.svg';

const Technology = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const pillars = [
    {
      title: 'Scalability',
      description: 'Our architecture is designed to handle millions of users and transactions, growing seamlessly with your business needs.',
      features: [
        'Microservices architecture',
        'Auto-scaling infrastructure',
        'Load balancing & CDN',
        'Database optimization'
      ],
      icon: (
        <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
        </svg>
      ),
      gradient: 'from-cyan-500 to-cyan-600',
    },
    {
      title: 'Security',
      description: 'Enterprise-grade security measures protect your data and ensure compliance with international standards.',
      features: [
        'End-to-end encryption',
        'Multi-factor authentication',
        'Regular security audits',
        'GDPR & SOC 2 compliance'
      ],
      icon: (
        <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
        </svg>
      ),
      gradient: 'from-coral-500 to-orange-500',
    },
    {
      title: 'Innovation',
      description: 'We leverage cutting-edge technologies and AI to deliver solutions that stay ahead of the curve.',
      features: [
        'AI & Machine Learning',
        'Real-time analytics',
        'Blockchain integration',
        'Mobile-first design'
      ],
      icon: (
        <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
      ),
      gradient: 'from-navy-500 to-navy-600',
    },
  ];

  const codeExample = `// Example: Real-time data processing
const processMarketData = async (data) => {
  const processed = await Promise.all(
    data.map(async (ticker) => {
      const analysis = await analyzeTrend(ticker);
      return {
        symbol: ticker.symbol,
        price: ticker.price,
        trend: analysis.direction,
        confidence: analysis.confidence
      };
    })
  );
  
  return processed.filter(t => t.confidence > 0.8);
};`;

  return (
    <section id="technology" className="section bg-gradient-to-b from-gray-50 to-white">
      <div className="container">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.6 }}
          className="text-center max-w-4xl mx-auto mb-20"
        >
          <h2 className="heading text-navy-950 mb-6">
            Our Technology
          </h2>
          <p className="text-xl text-gray-600 leading-relaxed">
            We combine technical expertise with creative problem-solving to deliver 
            innovative solutions that drive business growth and success across Africa.
          </p>
        </motion.div>

        {/* Technology Pillars */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-20">
          {pillars.map((pillar, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 50 }}
              animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
              transition={{ duration: 0.6, delay: index * 0.2 }}
              className="relative group"
            >
              <div className="card h-full group-hover:scale-105 transition-transform duration-300">
                <div className={`w-16 h-16 mb-6 bg-gradient-to-r ${pillar.gradient} rounded-2xl flex items-center justify-center text-white group-hover:scale-110 transition-transform duration-300`}>
                  {pillar.icon}
                </div>
                
                <h3 className="text-2xl font-display font-bold text-navy-950 mb-4">
                  {pillar.title}
                </h3>
                
                <p className="text-gray-600 leading-relaxed mb-6">
                  {pillar.description}
                </p>
                
                <div className="space-y-3">
                  {pillar.features.map((feature, featureIndex) => (
                    <div key={featureIndex} className="flex items-center text-sm text-gray-600">
                      <svg className="w-4 h-4 text-green-500 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      {feature}
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Code Example Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.6, delay: 0.8 }}
          className="bg-navy-950 rounded-3xl p-12 text-white"
        >
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h3 className="text-3xl font-display font-bold mb-6">
                Built for Performance
              </h3>
              <p className="text-xl text-gray-300 leading-relaxed mb-8">
                Our code is optimized for speed, reliability, and scalability. We use modern 
                development practices and cutting-edge technologies to ensure your applications 
                perform flawlessly under any load.
              </p>
              
              <div className="space-y-4">
                <div className="flex items-center">
                  <div className="w-3 h-3 bg-green-500 rounded-full mr-4"></div>
                  <span className="text-gray-300">Sub-second response times</span>
                </div>
                <div className="flex items-center">
                  <div className="w-3 h-3 bg-green-500 rounded-full mr-4"></div>
                  <span className="text-gray-300">99.9% uptime guarantee</span>
                </div>
                <div className="flex items-center">
                  <div className="w-3 h-3 bg-green-500 rounded-full mr-4"></div>
                  <span className="text-gray-300">Auto-scaling infrastructure</span>
                </div>
                <div className="flex items-center">
                  <div className="w-3 h-3 bg-green-500 rounded-full mr-4"></div>
                  <span className="text-gray-300">Real-time data processing</span>
                </div>
              </div>
            </div>
            
            <div className="relative">
              <div className="bg-gray-900 rounded-2xl p-6 overflow-hidden">
                <div className="flex items-center mb-4">
                  <div className="flex space-x-2">
                    <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                    <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                    <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                  </div>
                  <div className="ml-4 text-sm text-gray-400">market-processor.js</div>
                </div>
                <pre className="text-sm text-gray-300 font-mono overflow-x-auto">
                  <code>{codeExample}</code>
                </pre>
              </div>
              
              {/* Floating elements */}
              <div className="absolute -top-4 -right-4 w-8 h-8 bg-crimson-500 rounded-full animate-pulse"></div>
              <div className="absolute -bottom-4 -left-4 w-6 h-6 bg-coral-500 rounded-full animate-pulse animation-delay-2000"></div>
            </div>
          </div>
        </motion.div>

        {/* Development Team Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.6, delay: 1.0 }}
          className="mt-20"
        >
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={inView ? { opacity: 1, x: 0 } : { opacity: 0, x: -50 }}
              transition={{ duration: 0.6, delay: 1.2 }}
            >
              <img
                src={codingImage}
                alt="Developers writing code"
                className="w-full h-auto rounded-2xl shadow-xl"
              />
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={inView ? { opacity: 1, x: 0 } : { opacity: 0, x: 50 }}
              transition={{ duration: 0.6, delay: 1.4 }}
              className="space-y-6"
            >
              <h3 className="text-3xl font-display font-bold text-navy-950">
                Expert Development Team
              </h3>
              <p className="text-lg text-gray-600 leading-relaxed">
                Our world-class engineers bring decades of combined experience in building 
                scalable, secure, and innovative solutions. We combine technical excellence 
                with creative problem-solving to deliver exceptional results.
              </p>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="text-center p-4 bg-gray-50 rounded-xl">
                  <div className="text-2xl font-display font-bold text-crimson-600 mb-1">100%</div>
                  <div className="text-sm text-gray-600">Code Quality</div>
                </div>
                <div className="text-center p-4 bg-gray-50 rounded-xl">
                  <div className="text-2xl font-display font-bold text-crimson-600 mb-1">24/7</div>
                  <div className="text-sm text-gray-600">Support</div>
                </div>
              </div>
            </motion.div>
          </div>
        </motion.div>

        {/* Technical Capabilities */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.6, delay: 1.6 }}
          className="mt-20"
        >
          <div className="text-center mb-12">
            <h3 className="text-3xl font-display font-bold text-navy-950 mb-4">
              Technical Capabilities
            </h3>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              From cloud infrastructure to mobile development, we have the expertise to build 
              any solution your business needs.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              'Cloud Architecture',
              'Mobile Development',
              'Data Analytics',
              'API Development',
              'DevOps & CI/CD',
              'Database Design',
              'UI/UX Design',
              'Quality Assurance'
            ].map((capability, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                transition={{ duration: 0.6, delay: 1.8 + index * 0.05 }}
                className="card text-center group hover:scale-105 transition-transform duration-300"
              >
                <div className="text-2xl mb-3">⚡</div>
                <div className="text-sm font-medium text-gray-700">{capability}</div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Technology;
